import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { GitBranch } from "lucide-react";

const TRIGGER_LABELS = {
  booking_created: "Booking created",
  staff_edit: "Staff edited details",
  vehicle_reassigned: "Vehicle reassigned",
  backfill: "Backfilled (pre-history data)",
};

function fmt(dt) {
  if (!dt) return null;
  try { return new Date(dt).toLocaleString(); } catch { return dt; }
}

/**
 * Full confirmation-version history for a single booking (Phase 2C).
 * Phase 2E: pass `versions` to render pre-fetched, customer-safe data
 * (e.g. from GET /customer/bookings/{id}) instead of self-fetching the
 * staff-only endpoint: CustomerBookingDetail.jsx uses this.
 */
export default function ConfirmationHistoryPanel({ bookingId, versions: providedVersions }) {
  const [fetched, setFetched] = useState([]);
  const [loading, setLoading] = useState(!providedVersions);

  useEffect(() => {
    if (providedVersions) return;
    (async () => {
      try {
        const { data } = await api.get(`/bookings/${bookingId}/confirmation-versions`);
        setFetched(data.versions || []);
      } catch (_) { /* hide on permission */ }
      finally { setLoading(false); }
    })();
  }, [bookingId, providedVersions]);

  const versions = providedVersions || fetched;

  if (loading || versions.length === 0) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="confirmation-history-card">
      <h3 className="font-display text-lg font-semibold inline-flex items-center gap-2">
        <GitBranch className="w-4 h-4 text-[#1B4332]" /> Confirmation history
      </h3>
      <ol className="mt-3 relative border-l border-slate-200 ml-2 pl-4 space-y-3" data-testid="confirmation-version-list">
        {versions.slice().reverse().map((v) => {
          const isConfirmed = !!v.confirmed_at || v.was_confirmed;
          const dot = isConfirmed ? "bg-emerald-500" : v.superseded_at ? "bg-slate-400" : "bg-amber-500";
          return (
            <li key={v.id} className="text-sm relative" data-testid={`confirmation-version-${v.version_number}`}>
              <span className={`absolute -left-[22px] top-1 w-2.5 h-2.5 rounded-full ${dot}`} />
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="font-semibold text-slate-900">
                  Version {v.version_number}: {TRIGGER_LABELS[v.trigger] || v.trigger}
                </span>
                <span className="text-xs text-slate-500">{fmt(v.created_at)}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                <div>
                  Created by {v.created_by_type}
                  {fmt(v.sent_at) && ` · sent ${fmt(v.sent_at)}`}
                </div>
                {v.confirmed_at ? (
                  <div className="text-emerald-700">
                    Confirmed by {v.confirmed_by_type} · {fmt(v.confirmed_at)}
                  </div>
                ) : v.was_confirmed ? (
                  <div className="text-emerald-700">Confirmed (exact time not recorded: pre-history data)</div>
                ) : v.superseded_at ? (
                  <div>Superseded {fmt(v.superseded_at)}, never confirmed</div>
                ) : (
                  <div className="text-amber-700">Awaiting customer confirmation</div>
                )}
                {v.backfill_note && <div className="italic">{v.backfill_note}</div>}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
